/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

/** Which markup survives {@link sanitizeAllowlistedHtml}. Tag and attribute names are lowercase. */
export interface HtmlAllowlist {
    readonly tags: ReadonlySet<string>
    readonly attribute: (tag: string, name: string) => boolean
    /** Last check on a kept element, after its attributes are scrubbed; false unwraps it to its children. */
    readonly keep?: (el: Element, tag: string) => boolean
    /** Removes a kept element with its contents when this returns true, after its children are sanitized. */
    readonly drop?: (el: Element, tag: string) => boolean
    /** Removes a tag outside `tags` with its contents instead of unwrapping it to its children. */
    readonly dropDisallowed?: true
}

// Dropped with their contents: a script or style body surfaced as text is worse than nothing, and
// media never reaches a connected DOM with an `onerror`/`onload` carrier.
const DROP_WITH_CONTENT = new Set<string>([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'template',
    'noscript',
    'title',
    'textarea',
    'svg',
    'math',
    'link',
    'meta',
    'base',
    'form',
    'input',
    'select',
    'option',
    'img',
    'picture',
    'source',
    'audio',
    'video',
    'canvas',
])

/**
 * Keeps only the allowlisted tags and attributes of an untrusted HTML fragment. Disallowed tags
 * are unwrapped to their (sanitized) children, except the executable and media tags, which go with
 * their contents; comments are dropped. This is the DOMPurify model: the fragment is parsed in an
 * inert `<template>` and walked there, never by a regex.
 *
 * Every node stays in the template's inert owner document: moving a raw `<img onerror>` into the
 * live `document` would adopt it and fire the handler before it is stripped.
 */
export function sanitizeAllowlistedHtml(html: string, allowlist: HtmlAllowlist): string {
    if (!html) return ''
    const template = document.createElement('template')
    template.innerHTML = html
    // A wrapper, not the fragment: some DOMs don't reflect `content` mutations through `innerHTML`.
    const root = template.content.ownerDocument.createElement('div')
    root.append(...Array.from(template.content.childNodes))
    sanitizeChildren(root, allowlist)
    return root.innerHTML
}

function sanitizeChildren(node: Node, allowlist: HtmlAllowlist): void {
    for (const child of Array.from(node.childNodes)) sanitizeNode(child, allowlist)
}

function sanitizeNode(node: Node, allowlist: HtmlAllowlist): void {
    if (node.nodeType === 8 /* COMMENT_NODE */) {
        ;(node as ChildNode).remove()
        return
    }
    if (node.nodeType !== 1 /* ELEMENT_NODE */) return

    const el = node as Element
    const tag = el.tagName.toLowerCase()
    if (DROP_WITH_CONTENT.has(tag)) {
        el.remove()
        return
    }
    if (allowlist.dropDisallowed && !allowlist.tags.has(tag)) {
        el.remove()
        return
    }
    // Post-order, so an unwrapped parent hands up already-clean children.
    sanitizeChildren(el, allowlist)
    if (!allowlist.tags.has(tag)) {
        el.replaceWith(...Array.from(el.childNodes))
        return
    }
    for (const name of el.getAttributeNames()) {
        if (!allowlist.attribute(tag, name.toLowerCase())) el.removeAttribute(name)
    }
    if (allowlist.drop?.(el, tag)) {
        el.remove()
        return
    }
    if (allowlist.keep && !allowlist.keep(el, tag)) el.replaceWith(...Array.from(el.childNodes))
}
